import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, BarChart3, Gauge, RotateCcw, Building2, Clock, TrendingUp } from "lucide-react";
import { pretty } from "./external/referralConstants";

const SCORE_TONE = {
  excellent: "#2F7D5A", good: "#2C6480", needs_follow_up: "#B7791F", slow: "#9C1E22", not_enough_data: "#6B7280",
};
const A = (hex, a) => { const h = hex.replace("#", ""); return `rgba(${parseInt(h.slice(0,2),16)},${parseInt(h.slice(2,4),16)},${parseInt(h.slice(4,6),16)},${a})`; };

const Metric = ({ label, value }) => (
  <div><div className="text-[11px] text-[#9AA5B1] uppercase tracking-wide">{label}</div><div className="text-[15px] font-bold text-[#1C4358] tabular-nums">{value ?? "—"}</div></div>
);

export default function ExternalReferralsReport() {
  const nav = useNavigate();
  const [tab, setTab] = useState("report");
  const [report, setReport] = useState(null);
  const [scorecard, setScorecard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ account_type: "all", status: "all", service_lane: "all" });

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        account_type: f.account_type === "all" ? undefined : f.account_type,
        status: f.status === "all" ? undefined : f.status,
        service_lane: f.service_lane === "all" ? undefined : f.service_lane,
      };
      const { data } = await api.get("/reports/external-referrals", { params });
      setReport(data);
    } finally { setLoading(false); }
  }, [f]);
  useEffect(() => { loadReport(); }, [loadReport]);
  useEffect(() => { api.get("/reports/external-scorecard").then(({ data }) => setScorecard(data)).catch(() => {}); }, []);

  const t = report?.totals || {};
  const lanes = Object.keys(report?.by_service_lane || {}).filter((x) => x !== "unassigned");

  const TOTAL_TILES = [
    ["Open", t.open], ["Accepted", t.accepted], ["Declined", t.declined], ["Completed", t.completed],
    ["Cancelled", t.cancelled], ["Quote pending", t.quote_pending], ["Quote uploaded", t.quote_uploaded],
    ["Tour/appt pending", t.tour_appointment_pending], ["Tour/appt completed", t.tour_appointment_completed],
    ["Follow-ups due", t.follow_ups_due], ["Follow-ups overdue", t.follow_ups_overdue],
    ["Outcomes confirmed", t.outcomes_confirmed], ["Documents uploaded", t.documents_uploaded],
    ["Meetings completed", t.meetings_completed],
  ];

  return (
    <div className="pb-12" data-testid="external-reports-page">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <BarChart3 size={22} className="text-[#1C4358]" />
          <h1 className="text-2xl font-display font-bold text-[#1C4358]">External Referral Reports</h1>
        </div>
        <div className="flex gap-1.5">
          <button onClick={() => setTab("report")} data-testid="reports-tab-report"
            className={`text-[13px] font-semibold px-3 py-1.5 rounded-full border ${tab === "report" ? "bg-[#1C4358] text-white border-[#1C4358]" : "bg-white text-[#4B5563] border-[#E6EDF2]"}`}>Overview</button>
          <button onClick={() => setTab("scorecard")} data-testid="reports-tab-scorecard"
            className={`text-[13px] font-semibold px-3 py-1.5 rounded-full border ${tab === "scorecard" ? "bg-[#1C4358] text-white border-[#1C4358]" : "bg-white text-[#4B5563] border-[#E6EDF2]"}`}>Responsiveness scorecard</button>
        </div>
      </div>

      {tab === "report" && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2 items-center">
            <Select value={f.account_type} onValueChange={(v) => setF((p) => ({ ...p, account_type: v }))}>
              <SelectTrigger className="w-[170px] h-9 text-[13px]" data-testid="report-filter-account-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All account types</SelectItem>
                {(report?.filters?.account_types || []).map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={f.status} onValueChange={(v) => setF((p) => ({ ...p, status: v }))}>
              <SelectTrigger className="w-[180px] h-9 text-[13px]" data-testid="report-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {(report?.filters?.statuses || []).map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {lanes.length > 0 && (
              <Select value={f.service_lane} onValueChange={(v) => setF((p) => ({ ...p, service_lane: v }))}>
                <SelectTrigger className="w-[170px] h-9 text-[13px]" data-testid="report-filter-lane"><SelectValue placeholder="Service lane" /></SelectTrigger>
                <SelectContent><SelectItem value="all">All service lanes</SelectItem>{lanes.map((l) => <SelectItem key={l} value={l}>{pretty(l)}</SelectItem>)}</SelectContent>
              </Select>
            )}
            <Button variant="outline" size="sm" onClick={loadReport} className="border-[#D6E0E7] text-[#1C4358] gap-1"><RotateCcw size={14} /> Refresh</Button>
            <span className="text-[13px] text-[#4B5563] ml-auto">{report?.total ?? 0} referrals</span>
          </div>

          {loading && !report ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#1C4358]" /></div> : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2.5">
                {TOTAL_TILES.map(([label, val]) => (
                  <Card key={label} className="p-3 border-[#E6EDF2]" data-testid={`report-total-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    <div className="text-2xl font-bold text-[#1C4358] tabular-nums">{val ?? 0}</div>
                    <div className="text-[11px] text-muted-foreground leading-tight mt-0.5">{label}</div>
                  </Card>
                ))}
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <BreakdownCard title="By account type" data={report?.by_account_type} />
                <BreakdownCard title="By status" data={report?.by_status} />
                <BreakdownCard title="By service lane" data={report?.by_service_lane} hideUnassigned />
              </div>
            </>
          )}
        </div>
      )}

      {tab === "scorecard" && (
        <div className="space-y-3" data-testid="scorecard-list">
          {!scorecard ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#1C4358]" /></div> :
            (scorecard.scorecards || []).length === 0 ? <div className="text-center py-16 text-[#4B5563]">No external referral activity yet.</div> :
            scorecard.scorecards.map((c) => {
              const tone = SCORE_TONE[c.score] || "#6B7280";
              return (
                <Card key={c.account_id} className="p-4 border-[#E6EDF2]" data-testid={`scorecard-${c.account_id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                      <span className="h-9 w-9 rounded-lg bg-[#1C4358]/8 text-[#1C4358] flex items-center justify-center"><Building2 size={16} /></span>
                      <div>
                        <div className="font-semibold text-[#1C4358] text-[15px]">{c.organization}</div>
                        <div className="text-[12px] text-[#9AA5B1]">{c.account_type_label}{c.relationship_manager ? ` · RM: ${c.relationship_manager}` : ""}</div>
                      </div>
                    </div>
                    <span className="text-[12px] font-bold px-3 py-1 rounded-full" style={{ backgroundColor: A(tone, 0.14), color: tone }} data-testid={`scorecard-score-${c.account_id}`}>{c.score_label}</span>
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mt-3">
                    <Metric label="Sent" value={c.referrals_sent} />
                    <Metric label="Accepted" value={c.accepted} />
                    <Metric label="Declined" value={c.declined} />
                    <Metric label="Completed" value={c.completed} />
                    <Metric label="Accept rate" value={`${Math.round((c.acceptance_rate || 0) * 100)}%`} />
                    <Metric label="Complete rate" value={`${Math.round((c.completion_rate || 0) * 100)}%`} />
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mt-3 pt-3 border-t border-[#EEF3F6]">
                    <Metric label="Avg to view" value={c.avg_time_to_view} />
                    <Metric label="Avg to accept" value={c.avg_time_to_accept} />
                    <Metric label="Avg to quote" value={c.avg_time_to_quote} />
                    <Metric label="Avg to outcome" value={c.avg_time_to_outcome} />
                    <Metric label="Open / stalled" value={`${c.open} / ${c.stalled}`} />
                    <Metric label="Overdue" value={c.overdue_follow_ups} />
                  </div>
                </Card>
              );
            })}
        </div>
      )}
    </div>
  );
}

const BreakdownCard = ({ title, data, hideUnassigned }) => {
  const entries = Object.entries(data || {}).filter(([k]) => !(hideUnassigned && k === "unassigned"));
  const max = Math.max(1, ...entries.map(([, v]) => v));
  return (
    <Card className="p-4 border-[#E6EDF2]">
      <div className="text-[13px] font-semibold text-[#252221] mb-2 flex items-center gap-1.5"><TrendingUp size={14} /> {title}</div>
      {entries.length === 0 ? <div className="text-[12px] text-muted-foreground">No data.</div> : (
        <div className="space-y-1.5">
          {entries.map(([k, v]) => (
            <div key={k}>
              <div className="flex items-center justify-between text-[12px]"><span className="text-[#4B5563]">{pretty(k)}</span><span className="font-bold text-[#1C4358] tabular-nums">{v}</span></div>
              <div className="h-1.5 rounded-full bg-[#EEF3F6] mt-0.5"><div className="h-full rounded-full bg-[#1C4358]" style={{ width: `${(v / max) * 100}%` }} /></div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};
